import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/data/images";
import { PageHero } from "@/components/PageHero";
import { Stagger, item } from "@/components/Reveal";

const META = {
  departments: { base: "/activites", imgKey: "departments" },
  services: { base: "/services", imgKey: "services" },
  sectors: { base: "/solutions", imgKey: "sectors" },
  technologies: { base: "/technologies", imgKey: "technologies" },
};

export default function Detail({ collection }) {
  const { slug } = useParams();
  const { content } = useLang();
  const meta = META[collection];
  const list = content[collection];
  const idx = list.findIndex((p) => p.slug === slug);
  const page = list[idx];
  if (!page) return <Navigate to={meta.base} replace />;
  const image = IMAGES[meta.imgKey][slug];
  const prev = list[(idx - 1 + list.length) % list.length];
  const next = list[(idx + 1) % list.length];

  return (
    <>
      <PageHero label={page.name} title={page.heading || page.name} intro={page.intro} image={image} />

      <section className="bg-white" data-testid="detail-sections">
        <div className="container-x pb-20 md:pb-28">
          <Stagger className="max-w-4xl divide-y divide-[var(--line)]" stagger={0.08}>
            {page.sections.map((s, i) => (
              <motion.article key={s.title} variants={item} className="py-8 md:py-10" data-testid={`detail-section-${i}`}>
                <h3 data-testid={`detail-section-title-${i}`} className="font-head text-xl md:text-2xl font-medium tracking-tight text-black">{s.title}</h3>
                <div data-testid={`detail-section-description-${i}`} className="mt-3 space-y-4 text-sm md:text-[15px] leading-relaxed text-[var(--ink-2)]">
                  {s.text && s.text.split("\n\n").map((p, j) => <p key={j}>{p}</p>)}
                  {s.items && (
                    <ul className={typeof s.items[0] === "string" ? "grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2" : "space-y-5"} data-testid={`detail-section-items-${i}`}>
                      {s.items.map((it, j) => (
                        typeof it === "string" ? (
                          <li key={j} className="flex gap-3">
                            <span className="mt-[0.6rem] h-[3px] w-[3px] shrink-0 bg-[var(--ink-3)]" />
                            <span>{it}</span>
                          </li>
                        ) : (
                          <li key={j}>
                            <div className="earmark text-black">{it.label}</div>
                            <p className="mt-1.5">{it.text}</p>
                          </li>
                        )
                      ))}
                    </ul>
                  )}
                  {s.after && s.after.split("\n\n").map((p, j) => <p key={`a${j}`}>{p}</p>)}
                </div>
              </motion.article>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-[var(--bg-grey)] border-t border-[var(--line)]" data-testid="detail-pagination">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[var(--line)]">
          <Link to={`${meta.base}/${prev.slug}`} data-testid="detail-prev-link" className="group py-10 md:py-14 md:pr-10 flex items-center gap-5">
            <ArrowLeft className="text-[var(--ink-3)] group-hover:text-black group-hover:-translate-x-1 transition-all" />
            <div className="font-head text-xl md:text-2xl text-black">{prev.name}</div>
          </Link>
          <Link to={`${meta.base}/${next.slug}`} data-testid="detail-next-link" className="group py-10 md:py-14 md:pl-10 flex items-center justify-end gap-5 text-right">
            <div className="font-head text-xl md:text-2xl text-black">{next.name}</div>
            <ArrowRight className="text-[var(--ink-3)] group-hover:text-black group-hover:translate-x-1 transition-all" />
          </Link>
        </div>
      </section>
    </>
  );
}
